import React, { useEffect, useMemo, useState } from 'react';
import { EmptyDashboard } from '../organisms/EmptyDashboard';
import { StatsCard } from '../molecules/StatsCard';
import { PageHeader } from '../molecules/PageHeader';
import { PortfolioChart } from '../molecules/PortfolioChart';
import { PlusValueChart } from '../molecules/PlusValueChart';
import { PortfolioTreemap } from '../molecules/PortfolioTreemap';
import { DashboardQuickLinks } from '../molecules/DashboardQuickLinks';
import { ImportCSVModal } from '../organisms/ImportCSVModal';
import { usePayouts } from '../../utils/hooks/usePayouts';
import { useTokenPrices } from '../../utils/hooks/useTokenPrices';
import { formatCurrency } from '../../utils/date';
import { loadSettingsFromStorage } from '../../utils/storage';
import { useAuth } from '../../contexts/AuthContext';
import {
  calculateAveragePayoutsPerMonth,
  calculateAverageValue,
  calculateYearlyBNCSummary,
  preparePortfolioChartData,
  preparePlusValueChartData,
  type TokenData,
  type ChartDataPoint,
  type PlusValueDataPoint
} from '../../utils/dashboard';
import type { GlobalSettings } from '../../types/settings';

export const DashboardPage: React.FC = () => {
  // Load data with useQuery
  const payoutsQuery = usePayouts();
  const tokenPricesQuery = useTokenPrices(payoutsQuery.data);
  const { isAuthenticated } = useAuth();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [settings, setSettings] = useState<GlobalSettings | null>(null);

  const payouts = payoutsQuery.data || [];
  const tokens = (tokenPricesQuery.data as TokenData[]) || [];
  const loading = payoutsQuery.isLoading || tokenPricesQuery.isLoading;

  // Load settings (needed for the configured BNC rate)
  useEffect(() => {
    loadSettingsFromStorage().then(setSettings);
  }, []);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalCurrentValue = tokens.reduce((sum, token) => {
      return sum + (token.currentTotalValue || 0);
    }, 0);

    return {
      totalPayouts: payouts.length,
      averagePayoutsPerMonth: calculateAveragePayoutsPerMonth(payouts),
      averagePayoutValue: calculateAverageValue(payouts),
      totalCurrentValue: totalCurrentValue
    };
  }, [payouts, tokens]);

  const yearlyBNC = useMemo(
    () => calculateYearlyBNCSummary(payouts, settings?.bncRate ?? 0.34),
    [payouts, settings]
  );

  // Prepare chart data (last 12 months)
  const portfolioChartData = useMemo<ChartDataPoint[]>(() => {
    if (payouts.length === 0 || tokens.length === 0) return [];
    return preparePortfolioChartData(payouts, tokens, 12);
  }, [payouts, tokens]);

  const plusValueChartData = useMemo<PlusValueDataPoint[]>(() => {
    if (payouts.length === 0 || tokens.length === 0) return [];
    return preparePlusValueChartData(payouts, tokens, 12);
  }, [payouts, tokens]);

  // Empty state
  if (!loading && payouts.length === 0) {
    return <EmptyDashboard />;
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-8">
      <ImportCSVModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        existingPayouts={payouts}
      />

      <div className="flex items-start justify-between mb-0">
        <PageHeader title="Dashboard" subtitle="Vue d'ensemble de votre portfolio crypto" variant="left" />
        {isAuthenticated && (
          <button
            onClick={() => setIsImportOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors mt-8"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 11l3 3m0 0l3-3m-3 3V4" />
            </svg>
            Importer CSV
          </button>
        )}
      </div>

      {/* Data Storage Notice - Only show for guests */}
      {!isAuthenticated && (
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 pt-0.5">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">
                Stockage des données
              </h3>
              <p className="text-sm text-blue-700 mb-3">
                Vos données sont stockées localement dans votre navigateur. Vous pouvez les exporter, les importer ou les supprimer définitivement depuis la page <strong>Configuration</strong>.
              </p>
              <a
                href="/parametres"
                className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                Accéder à la Configuration
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Alpha Warning */}
      <div className="mb-8 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-300 rounded-lg p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 pt-0.5">
            <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-yellow-900 mb-1">
              ⚠️ Application en version Alpha
            </h3>
            <p className="text-sm text-yellow-800 mb-2">
              CryptoTrack est actuellement en version Alpha. Veuillez <strong>vérifier attentivement tous vos enregistrements</strong> et vous assurer que les données sont exactes avant de les utiliser à des fins fiscales ou de déclaration.
            </p>
            <p className="text-xs text-yellow-700">
              Les bugs et imprécisions peuvent exister. Testez toutes vos données critiques et consultez un expert comptable avant de déclarer vos impôts.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Chargement des données...</p>
          </div>
        </div>
      ) : (
        <>
          {/* KPI Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatsCard
              title="Total Transactions"
              value={stats.totalPayouts}
              suffix=""
              color="blue"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />

            <StatsCard
              title="Moyenne par mois"
              value={stats.averagePayoutsPerMonth}
              suffix="payouts"
              color="purple"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              }
            />

            <StatsCard
              title="Valeur moyenne"
              value={formatCurrency(stats.averagePayoutValue)}
              suffix=""
              color="orange"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              }
            />

            <StatsCard
              title="Valeur actuelle"
              value={formatCurrency(stats.totalCurrentValue)}
              suffix=""
              color="green"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              }
            />
          </div>

          {/* BNC / Fiscalité - Année en cours */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <StatsCard
              title="Total BNC (année en cours)"
              value={formatCurrency(yearlyBNC.bncTotal)}
              suffix=""
              color="purple"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />

            <StatsCard
              title="URSSAF estimée (année en cours)"
              value={formatCurrency(yearlyBNC.estimatedTax)}
              suffix=""
              color="orange"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              }
            />

            <StatsCard
              title="Cessions imposables (année en cours)"
              value={formatCurrency(yearlyBNC.taxableSalesTotal)}
              suffix=""
              color="red"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              }
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <PortfolioChart data={portfolioChartData} isLoading={loading} />
            <PlusValueChart data={plusValueChartData} isLoading={loading} />
          </div>

          {/* Portfolio breakdown */}
          <div className="grid grid-cols-1 gap-6 mt-6">
            <PortfolioTreemap tokens={tokens} isLoading={loading} />
          </div>

          {/* Quick Links */}
          <DashboardQuickLinks />
        </>
      )}
    </div>
  );
};
